import React, { useState } from "react";
import api from "../services/api";
import styles from "./ProfileModal.module.css";
import ModalHeader from "./ModalHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation, faCheck, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import PasswordRequirementsChecklist, { isPasswordStrong } from "./PasswordRequirementsChecklist";

/**
 * Modal obrigatório de troca de senha — exibido quando user.must_change_password
 * é true (aluno criado via convite, ainda usando a senha temporária enviada por
 * email). Não pode ser fechado sem trocar a senha: sem botão "Cancelar", sem X
 * no header (ModalHeader com disabled=true), sem fechar no backdrop/Escape.
 *
 * Props:
 *   user       – usuário atual (precisa de auth_provider pra saber se pede senha atual)
 *   onChanged  – (patch) => void — chamado com { must_change_password: false } após sucesso
 */
export default function ForcePasswordChangeModal({ user, onChanged }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword]         = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd]                 = useState(false);
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState("");

  useBodyScrollLock();

  const hasPassword = user?.auth_provider === "hybrid" || user?.auth_provider === "local";

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (hasPassword && !currentPassword) {
      setError("Informe a senha temporária que você recebeu por email.");
      return;
    }
    if (!isPasswordStrong(newPassword)) {
      setError("A nova senha precisa ter pelo menos 8 caracteres, 1 maiúscula, 1 número e 1 caractere especial.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("As senhas não conferem.");
      return;
    }
    if (hasPassword && currentPassword === newPassword) {
      setError("A nova senha deve ser diferente da senha temporária.");
      return;
    }

    setSaving(true);
    try {
      await api.put("/users/me/password", {
        ...(hasPassword ? { current_password: currentPassword } : {}),
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      onChanged?.({ must_change_password: false });
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível trocar a senha. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.backdrop} role="presentation">
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Troca de senha obrigatória"
      >
        <ModalHeader onClose={() => {}} disabled />

        <div className={styles.header}>
          <div>
            <div className={styles.title}>Defina uma nova senha</div>
            <div className={styles.sub}>
              Por segurança, você precisa trocar a senha temporária antes de continuar.
            </div>
          </div>
        </div>

        <form className={styles.body} onSubmit={submit}>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              background: "var(--border-0-06)",
              border: "1px solid var(--border-0-10)",
              borderRadius: 10,
              padding: "10px 12px",
              marginBottom: 14,
              fontSize: 13,
              color: "var(--text-0-70)",
            }}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginTop: 2, color: "var(--accent)" }} />
            <span>
              {hasPassword
                ? "Digite a senha temporária enviada no email de convite (não a que você escolheu agora) e defina uma nova senha só sua."
                : "Defina uma senha para poder entrar sem depender só do Google."}
            </span>
          </div>

          {hasPassword && (
            <div className={styles.field} style={{ marginTop: 4 }}>
              <label className={styles.label}>Senha temporária (recebida por email ou professor)</label>
              <div className={styles.pwdWrap}>
                <input
                  type={showPwd ? "text" : "password"}
                  className={styles.input}
                  value={currentPassword}
                  onChange={(e) => { setCurrentPassword(e.target.value); setError(""); }}
                  placeholder="Senha temporária"
                  autoComplete="current-password"
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className={styles.field} style={{ marginTop: 4 }}>
            <label className={styles.label}>Nova senha</label>
            <div className={styles.pwdWrap}>
              <input
                type={showPwd ? "text" : "password"}
                className={styles.input}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
                placeholder="Mínimo 8 caracteres"
                autoComplete="new-password"
              />
              <button type="button" className={styles.pwdToggle} onClick={() => setShowPwd((v) => !v)} tabIndex={-1}>
                <FontAwesomeIcon icon={showPwd ? faEyeSlash : faEye} />
              </button>
            </div>
            {newPassword && <PasswordRequirementsChecklist password={newPassword} />}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Confirmar nova senha</label>
            <div className={styles.pwdWrap}>
              <input
                type={showPwd ? "text" : "password"}
                className={styles.input}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                placeholder="Repita a senha"
                autoComplete="new-password"
              />
            </div>
          </div>

          {error && <p className={styles.pwdError}>{error}</p>}

          <button
            type="submit"
            className={styles.btnPwdSave}
            disabled={saving || !isPasswordStrong(newPassword) || !confirmPassword || (hasPassword && !currentPassword)}
          >
            {saving ? (
              "Salvando..."
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} /> Trocar senha e continuar
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
